/*
 * Fitting Buchung
 * Custom calendar: loads available days per month (one API call),
 * greys out unavailable days before the user clicks.
 * Day-level slot loading on date selection, hold + add-to-cart unchanged.
 */
if (!customElements.get('fitting-booking-form')) {
  const MONTHS_DE = ['Januar','Februar','März','April','Mai','Juni',
                     'Juli','August','September','Oktober','November','Dezember'];

  // Texte je Sprache aus data-fb-i18n (fitting-booking-form.liquid). Die
  // deutschen Werte hier sind der Rueckfall. Optionswerte, die in die Bestellung
  // gehen (Fokus usw.), bleiben deutsch; uebersetzt wird nur die Anzeige.
  const FB_TEXTE_DE = {
    nextSlot: 'Nächster freier Termin',
    datePickerAria: 'Datumsauswahl',
    prevMonth: 'Vorheriger Monat',
    nextMonth: 'Nächster Monat',
    weekdays: 'Mo,Di,Mi,Do,Fr,Sa,So',
    months: MONTHS_DE.join(','),
    dayLabel: '[day]. [month] [year]',
    unavailableSuffix: ' – nicht verfügbar',
    chooseScopeFirst: 'Bitte zuerst Fitting-Umfang wählen.',
    chooseScopeFirstOption: 'Bitte zuerst Fitting-Umfang wählen',
    loadingDays: 'Lade verfügbare Tage…',
    loadingSlots: 'Lade verfügbare Slots…',
    slotsError: 'Slots konnten nicht geladen werden. Bitte später erneut versuchen.',
    noSlots: 'Für diesen Tag sind keine Slots verfügbar.',
    chooseDate: 'Bitte ein Datum wählen.',
    holdFailed: 'Slot konnte nicht reserviert werden. Bitte erneut auswählen.',
    holdExpired: 'Reservierung abgelaufen – dein Slot wird beim Bezahlen automatisch erneut geprüft.',
    holdActive: 'Dein Termin ist für dich reserviert · noch [time] Min',
    requiredMissing: 'Bitte fülle die rot markierten Pflichtfelder aus.',
    configError: 'Konfigurationsfehler: Varianten-ID fehlt. Bitte Support kontaktieren.',
    preparing: 'Buchung wird vorbereitet…',
    bookingFailed: 'Buchung konnte nicht angelegt werden. Bitte erneut versuchen.',
    errorStatus: 'Fehler [status]',
    pleaseWait: 'Bitte warten…',
    submit: 'Weiter zur Bezahlung',
    focus: {}
  };

  const fbTexteLaden = (root) => {
    const texte = Object.assign({}, FB_TEXTE_DE, { focus: {} });
    const el = root.querySelector('[data-fb-i18n]');
    if (!el) return texte;
    try {
      const geladen = JSON.parse(el.textContent);
      const dekodierer = document.createElement('textarea');
      const dekodieren = (s) => { dekodierer.innerHTML = s; return dekodierer.value; };
      Object.keys(FB_TEXTE_DE).forEach((k) => {
        if (k !== 'focus' && typeof geladen[k] === 'string' && geladen[k]) texte[k] = dekodieren(geladen[k]);
      });
      if (geladen.focus && typeof geladen.focus === 'object') {
        Object.keys(geladen.focus).forEach((k) => {
          if (typeof geladen.focus[k] === 'string' && geladen.focus[k]) texte.focus[k] = dekodieren(geladen.focus[k]);
        });
      }
    } catch (e) {
      /* Rueckfall auf Deutsch */
    }
    return texte;
  };

  class FittingBookingForm extends HTMLElement {
    constructor() {
      super();
      this.form           = this.querySelector('.fb-form');
      this.dateInput      = this.querySelector('#fb-date');
      this.slotsEl        = this.querySelector('[data-fb-slots]');
      this.slotIdInput    = this.querySelector('[data-fb-slot-id]');
      this.slotLabelInput = this.querySelector('[data-fb-slot-label]');
      this.submitBtn      = this.querySelector('[data-fb-submit]');
      this.submitLabel    = this.querySelector('[data-fb-submit-label]');
      this.statusEl       = this.querySelector('[data-fb-status]');
      this.focusSelect    = this.querySelector('[data-fb-focus]');
      this.bagNote        = this.querySelector('[data-fb-bag-note]');
      this.dateHint       = this.querySelector('[data-fb-date-hint]');
      this.nextSlotEl     = this.querySelector('[data-fb-next-slot]');
      this.texte          = fbTexteLaden(this);
      this.monate         = this.texte.months.split(',');

      this.middlewareUrl = (this.dataset.middlewareUrl || '').replace(/\/$/, '');
      this.variantIds = {
        '1h':     this.dataset.variant1h,
        full_bag: this.dataset.variantFullBag
      };
      this.locationId = this.dataset.locationId || 'default';

      this.selectedSlot  = null;
      this.selectedDate  = null;
      this.calYear       = null;
      this.calMonth      = null;
      this.calRenderSeq  = 0;
      this.calEl         = null;
      this.calGrid       = null;
      this.calTitle      = null;
      this.availableDaysCache = new Map(); // "YYYY-MM|dur" -> { [date]: count }

      const now = new Date();
      this.calYear  = now.getFullYear();
      this.calMonth = now.getMonth();

      this.setupDateBounds();
      this.bindEvents();
      this.buildCalendar();
      this.syncDurationDependent();
      this.loadNextSlot();
    }

    /* ─── "Nächster freier Termin"-Teaser (rein informativ) ─── */

    async loadNextSlot() {
      if (!this.middlewareUrl || !this.nextSlotEl) return;
      try {
        const res = await fetch(this.middlewareUrl + '/next-slot', { credentials: 'omit' });
        if (!res.ok) return;
        const data = await res.json();
        const ns = data && data.next_slot;
        if (ns && ns.label) {
          // Der Worker liefert das Label deutsch ("Fr., 23.10. um 16:00 Uhr"); ausserhalb DE aus starts_at in der Seitensprache bauen.
          let label = ns.label;
          const lang = (document.documentElement.lang || 'de').toLowerCase().replace(/^en$/, 'en-gb');
          if (ns.starts_at && !lang.startsWith('de')) {
            try {
              const d = new Date(ns.starts_at);
              const tag = new Intl.DateTimeFormat(lang, { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' }).format(d);
              const zeit = new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Berlin' }).format(d);
              label = tag + ', ' + zeit;
            } catch (e) { /* deutsches Label behalten */ }
          }
          this.nextSlotEl.innerHTML =
            '<span class="fb-next-slot__dot"></span>' +
            '<span class="fb-next-slot__label">' + this.texte.nextSlot + ' </span>' +
            '<span class="fb-next-slot__date">' + label + '</span>';
          this.nextSlotEl.hidden = false;
        }
      } catch (err) {
        // Teaser darf das Laden der Seite oder den Buchungsflow NIE blockieren.
        console.warn('[fitting-booking] loadNextSlot failed', err);
      }
    }

    /* ─── Calendar ─── */

    buildCalendar() {
      this.dateInput.type = 'hidden';
      this.dateInput.removeAttribute('required');

      const cal = document.createElement('div');
      cal.className = 'fb-cal';
      cal.setAttribute('role', 'group');
      cal.setAttribute('aria-label', this.texte.datePickerAria);
      cal.innerHTML =
        '<div class="fb-cal__header">' +
          '<button type="button" class="fb-cal__nav" data-cal-prev aria-label="' + this.texte.prevMonth + '">&#8249;</button>' +
          '<span class="fb-cal__title" data-cal-title></span>' +
          '<button type="button" class="fb-cal__nav" data-cal-next aria-label="' + this.texte.nextMonth + '">&#8250;</button>' +
        '</div>' +
        '<div class="fb-cal__weekdays">' +
          this.texte.weekdays.split(',').map(d => '<span class="fb-cal__wd">' + d + '</span>').join('') +
        '</div>' +
        '<div class="fb-cal__grid" data-cal-grid></div>';

      this.dateInput.after(cal);
      if (this.dateHint) this.dateHint.hidden = true;

      this.calEl    = cal;
      this.calGrid  = cal.querySelector('[data-cal-grid]');
      this.calTitle = cal.querySelector('[data-cal-title]');
      this.calWeekdays = cal.querySelector('.fb-cal__weekdays');

      cal.querySelector('[data-cal-prev]').addEventListener('click', () => this.calNav(-1));
      cal.querySelector('[data-cal-next]').addEventListener('click', () => this.calNav(+1));

      this.applyCalLayout();
      window.addEventListener('resize', () => this.applyCalLayout());
    }

    applyCalLayout() {
      // Volle Breite auf allen Viewports; Zellhoehe/Radius regelt das CSS.
      this.calEl.style.width = '100%';
      this.calGrid.style.gridTemplateColumns = 'repeat(7, 1fr)';
      this.calWeekdays.style.gridTemplateColumns = 'repeat(7, 1fr)';
    }

    calNav(delta) {
      this.calMonth += delta;
      if (this.calMonth < 0)  { this.calMonth = 11; this.calYear--; }
      if (this.calMonth > 11) { this.calMonth = 0;  this.calYear++; }
      this.renderCalendar();
    }

    async renderCalendar() {
      if (!this.calGrid) return;

      const seq   = ++this.calRenderSeq;
      const year  = this.calYear;
      const month = this.calMonth;

      this.calTitle.textContent = this.monate[month] + ' ' + year;

      if (!this.duration) {
        this.calGrid.innerHTML = '<p class="fb-cal__loading">' + this.texte.chooseScopeFirst + '</p>';
        return;
      }

      this.calGrid.innerHTML = '<p class="fb-cal__loading">' + this.texte.loadingDays + '</p>';

      const available = await this.loadAvailableDays(year, month);
      if (seq !== this.calRenderSeq) return;

      const today      = berlinToday();
      const maxDate    = new Date();
      maxDate.setDate(maxDate.getDate() + 60);
      const maxDateStr = berlinDay(maxDate.toISOString());

      const firstDow    = new Date(year, month, 1).getDay();
      const padding     = firstDow === 0 ? 6 : firstDow - 1;
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      this.calGrid.innerHTML = '';

      for (let i = 0; i < padding; i++) {
        const blank = document.createElement('span');
        blank.className = 'fb-cal__day fb-cal__day--other-month';
        blank.setAttribute('aria-hidden', 'true');
        this.calGrid.appendChild(blank);
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr  = year + '-' + pad2(month + 1) + '-' + pad2(d);
        const count    = available[dateStr] ?? 0;
        const hasSlots = count > 0;
        const disabled = !hasSlots || dateStr < today || dateStr > maxDateStr;
        const selected = dateStr === this.selectedDate;
        const isToday  = dateStr === today;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fb-cal__day';
        if (selected)       btn.classList.add('fb-cal__day--selected');
        else if (isToday)   btn.classList.add('fb-cal__day--today');
        if (disabled)       btn.classList.add('fb-cal__day--disabled');
        btn.disabled = disabled;
        btn.setAttribute('aria-label', this.texte.dayLabel.replace('[day]', d).replace('[month]', this.monate[month]).replace('[year]', year) + (disabled ? this.texte.unavailableSuffix : ''));
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');

        const num = document.createElement('span');
        num.textContent = String(d);
        btn.appendChild(num);

        if (hasSlots && !disabled) {
          const badge = document.createElement('span');
          badge.className = 'fb-cal__badge';
          badge.setAttribute('aria-hidden', 'true');
          badge.textContent = count > 9 ? '9+' : String(count);
          btn.appendChild(badge);
        }

        if (!disabled) btn.addEventListener('click', () => this.selectDate(dateStr));
        this.calGrid.appendChild(btn);
      }
    }

    async loadAvailableDays(year, month) {
      const dur = this.duration || '1h';
      const key = year + '-' + pad2(month + 1) + '|' + dur;
      if (this.availableDaysCache.has(key)) return this.availableDaysCache.get(key);

      if (!this.middlewareUrl) {
        const mock  = {};
        const today = berlinToday();
        const days  = new Date(year, month + 1, 0).getDate();
        for (let d = 1; d <= days; d++) {
          const dateStr = year + '-' + pad2(month + 1) + '-' + pad2(d);
          const dow = new Date(year, month, d).getDay();
          if (dow !== 0 && dateStr >= today) mock[dateStr] = Math.ceil(Math.random() * 5);
        }
        this.availableDaysCache.set(key, mock);
        return mock;
      }

      const from = year + '-' + pad2(month + 1) + '-01';
      const to   = year + '-' + pad2(month + 1) + '-' + pad2(new Date(year, month + 1, 0).getDate());
      try {
        const res  = await fetch(
          this.middlewareUrl + '/slots?from=' + from + '&to=' + to + '&duration=' + encodeURIComponent(dur),
          { credentials: 'omit' }
        );
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const days = data?.availableDays ?? {};
        this.availableDaysCache.set(key, days);
        return days;
      } catch (err) {
        console.error('[fitting-booking] loadAvailableDays', err);
        return {};
      }
    }

    selectDate(dateStr) {
      this.selectedDate    = dateStr;
      this.dateInput.value = dateStr;
      if (this.calEl) this.calEl.classList.remove('fb-invalid');
      this.clearSlot();
      this.slotsEl.innerHTML = '<p class="fb-slots__hint">' + this.texte.loadingSlots + '</p>';
      this.renderCalendar();
      this.loadSlots();
    }

    /* ─── Duration sync ─── */

    syncDurationDependent() {
      const d = this.duration;

      if (this.bagNote) this.bagNote.hidden = d !== 'full_bag';

      if (this.focusSelect) {
        if (!d) {
          this.focusSelect.disabled = true;
          this.focusSelect.innerHTML = '<option value="">' + this.texte.chooseScopeFirstOption + '</option>';
        } else {
          this.focusSelect.disabled = false;
          const options = FOCUS_OPTIONS[d] || FOCUS_OPTIONS['1h'];
          const current = this.focusSelect.value;
          this.focusSelect.innerHTML = '';
          options.forEach((o) => {
            const opt = document.createElement('option');
            opt.value       = o.value;
            opt.textContent = (o.value ? this.texte.focus[o.value] : this.texte.focus.please_select) || o.label;
            this.focusSelect.appendChild(opt);
          });
          if (options.some((o) => o.value === current)) this.focusSelect.value = current;
        }
      }

      if (!d && this.slotsEl) {
        this.clearSlot();
        this.slotsEl.innerHTML = '<p class="fb-slots__hint">' + this.texte.chooseScopeFirst + '</p>';
      }

      this.renderCalendar();
    }

    /* ─── Date bounds ─── */

    setupDateBounds() {
      const today = new Date();
      const max   = new Date();
      max.setDate(max.getDate() + 60);
      this.dateInput.min = isoDate(today);
      this.dateInput.max = isoDate(max);
    }

    /* ─── Events ─── */

    bindEvents() {
      this.form.querySelectorAll('input[name="duration"]').forEach((r) =>
        r.addEventListener('change', () => {
          this.selectedDate    = null;
          this.dateInput.value = '';
          this.clearSlot();
          this.slotsEl.innerHTML = '<p class="fb-slots__hint">' + this.texte.chooseDate + '</p>';
          this.syncDurationDependent();
        })
      );
      // Rote Markierung entfernen, sobald ein Feld gültig wird.
      this.form.querySelectorAll('input, select, textarea').forEach((field) => {
        const clear = () => this.clearFieldError(field);
        field.addEventListener('blur', clear);
        field.addEventListener('change', clear);
        field.addEventListener('input', clear);
      });

      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    get duration() {
      return this.form.querySelector('input[name="duration"]:checked')?.value || null;
    }

    /* ─── Slot loading (day-level) ─── */

    async loadSlots() {
      const date = this.selectedDate;
      if (!date) return;
      this.clearSlot();
      this.slotsEl.innerHTML = '<p class="fb-slots__hint">' + this.texte.loadingSlots + '</p>';
      try {
        const slots = await this.fetchSlots(date, this.duration);
        this.renderSlots(slots);
      } catch (err) {
        console.error('[fitting-booking] loadSlots', err);
        this.slotsEl.innerHTML =
          '<p class="fb-slots__hint">' + this.texte.slotsError + '</p>';
      }
    }

    async fetchSlots(date, duration) {
      if (!this.middlewareUrl) return mockSlots(date, duration);
      const url = this.middlewareUrl + '/slots?date=' + encodeURIComponent(date) + '&duration=' + encodeURIComponent(duration);
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error('slots ' + res.status);
      const data = await res.json();
      return Array.isArray(data?.slots) ? data.slots : [];
    }

    renderSlots(slots) {
      if (!slots.length) {
        this.slotsEl.innerHTML = '<p class="fb-slots__hint">' + this.texte.noSlots + '</p>';
        return;
      }
      this.slotsEl.innerHTML = '';
      slots.forEach((s) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fb-slot';
        btn.textContent = s.label;
        btn.setAttribute('aria-pressed', 'false');
        btn.dataset.slotId    = s.id;
        btn.dataset.slotLabel = s.label;
        if (s.available === false) btn.disabled = true;
        btn.addEventListener('click', () => this.selectSlot(btn));
        this.slotsEl.appendChild(btn);
      });
    }

    clearSlot() {
      // Free the abandoned hold when the user changes date/duration.
      if (this.selectedSlot && this.selectedSlot.holdId) this.releaseHold(this.selectedSlot.holdId);
      this.selectedSlot         = null;
      this.slotIdInput.value    = '';
      this.slotLabelInput.value = '';
      this.slotsEl.querySelectorAll('.fb-slot[aria-pressed="true"]').forEach((el) =>
        el.setAttribute('aria-pressed', 'false')
      );
      this.stopHoldCountdown();
      if (this.holdBanner) this.holdBanner.hidden = true;
    }

    async selectSlot(btn) {
      const slotId    = btn.dataset.slotId;
      const slotLabel = btn.dataset.slotLabel;

      // Re-click of the slot we already hold: keep the live hold + countdown.
      // (A fresh hold attempt on a slot we hold ourselves would 409.)
      if (this.selectedSlot && this.selectedSlot.id === slotId && this.selectedSlot.holdId) {
        return;
      }

      this.slotsEl.querySelectorAll('.fb-slot[aria-pressed="true"]').forEach((el) =>
        el.setAttribute('aria-pressed', 'false')
      );
      btn.setAttribute('aria-pressed', 'true');

      // Switching slots: release the previous hold so we never hog a slot we left.
      const previousHoldId = this.selectedSlot && this.selectedSlot.holdId;
      if (previousHoldId) this.releaseHold(previousHoldId);

      this.selectedSlot = { id: slotId, label: slotLabel, holdId: null };
      this.slotIdInput.value    = slotId;
      this.slotLabelInput.value = slotLabel;
      this.slotsEl.classList.remove('fb-invalid');
      try {
        const hold = await this.softHold(slotId);
        if (hold) {
          this.selectedSlot.holdId = hold.holdId;
          this.startHoldCountdown(hold.expiresAt);
        }
      } catch (err) {
        console.warn('[fitting-booking] softHold failed', err);
        this.setStatus(this.texte.holdFailed, true);
      }
    }

    releaseHold(holdId) {
      if (!this.middlewareUrl || !holdId) return;
      // Best-effort, fire-and-forget: frees the slot we navigated away from so
      // browsing several slots doesn't leave them all reserved.
      fetch(this.middlewareUrl + '/hold/' + encodeURIComponent(holdId), {
        method: 'DELETE',
        credentials: 'omit'
      }).catch((err) => console.warn('[fitting-booking] releaseHold failed', err));
    }

    async softHold(slotId) {
      if (!this.middlewareUrl) return null;
      const sessionId = await this.getCartToken();
      const holdBody = { slot_id: slotId, duration: this.duration };
      if (sessionId) holdBody.session_id = sessionId;
      const res = await fetch(this.middlewareUrl + '/hold', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(holdBody)
      });
      if (!res.ok) throw new Error('hold ' + res.status);
      const data = await res.json();
      if (!data?.hold_id) return null;
      return { holdId: data.hold_id, expiresAt: data.expires_at || null };
    }

    /* ─── Hold-Countdown ─── */

    ensureHoldBanner() {
      if (this.holdBanner) return;
      const el = document.createElement('div');
      el.className = 'fb-hold';
      el.hidden = true;
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.innerHTML =
        '<span class="fb-hold__icon" aria-hidden="true">&#9201;</span>' +
        '<span class="fb-hold__text" data-fb-hold-text></span>';
      this.slotsEl.insertAdjacentElement('afterend', el);
      this.holdBanner = el;
      this.holdTextEl = el.querySelector('[data-fb-hold-text]');
    }

    startHoldCountdown(expiresAt) {
      this.ensureHoldBanner();
      this.stopHoldCountdown();
      const parsed = expiresAt ? Date.parse(expiresAt) : NaN;
      // Fallback auf 30 Min ab jetzt, falls MB keine nutzbare expires_at liefert.
      this.holdExpiry = Number.isFinite(parsed) ? parsed : Date.now() + 30 * 60 * 1000;
      this.holdBanner.hidden = false;
      this.holdBanner.classList.remove('fb-hold--expired');
      this.tickHold();
      this.holdInterval = window.setInterval(() => this.tickHold(), 1000);
    }

    tickHold() {
      if (!this.holdTextEl) return;
      const msLeft = this.holdExpiry - Date.now();
      if (msLeft <= 0) {
        this.stopHoldCountdown();
        this.holdBanner.classList.add('fb-hold--expired');
        this.holdTextEl.textContent = this.texte.holdExpired;
        return;
      }
      const totalSec = Math.floor(msLeft / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      this.holdTextEl.textContent =
        this.texte.holdActive.replace('[time]', m + ':' + String(s).padStart(2, '0'));
    }

    stopHoldCountdown() {
      if (this.holdInterval) {
        window.clearInterval(this.holdInterval);
        this.holdInterval = null;
      }
    }


    async getCartToken() {
      try {
        const res = await fetch('/cart.js');
        if (!res.ok) return null;
        const cart = await res.json();
        const token = cart?.token;
        return token ? token.split('?')[0] : null;
      } catch {
        return null;
      }
    }

    /* ─── Submit ─── */

    async handleSubmit(e) {
      e.preventDefault();
      this.setStatus('');

      const invalid = this.validateForm();
      if (invalid.length) {
        this.setStatus(this.texte.requiredMissing, true);
        // Zum ersten fehlenden Feld in Dokumentreihenfolge scrollen.
        invalid.sort((a, b) =>
          a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
        );
        const first = invalid[0];
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusEl = first.matches('input, select, textarea')
          ? first
          : first.querySelector('input:not([type=hidden]), select, textarea');
        if (focusEl) {
          window.setTimeout(() => { try { focusEl.focus({ preventScroll: true }); } catch (_) {} }, 320);
        }
        return;
      }

      const duration  = this.duration;
      const variantId = this.variantIds[duration];
      if (!variantId) {
        this.setStatus(this.texte.configError, true);
        return;
      }

      this.lockSubmit(true);
      this.setStatus(this.texte.preparing);

      try {
        const properties = this.collectProperties();
        await this.addToCart(variantId, properties);
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[fitting-booking] submit', err);
        this.setStatus(err?.message || this.texte.bookingFailed, true);
        this.lockSubmit(false);
      }
    }

    /**
     * Prüft alle Pflichtfelder, markiert ALLE fehlenden mit .fb-invalid und gibt
     * die zu scrollenden Elemente zurück. Der Slot wird über den Formular-Wert
     * (verstecktes slot_id-Feld) geprüft, nicht nur über das JS-Objekt — so wird
     * ein reservierter Slot zuverlässig als gültig erkannt.
     */
    validateForm() {
      const invalid = [];
      this.querySelectorAll('.fb-invalid').forEach((el) => el.classList.remove('fb-invalid'));
      const mark = (el) => { if (el && !invalid.includes(el)) { el.classList.add('fb-invalid'); invalid.push(el); } };

      // Datum (Custom-Kalender)
      if (!this.selectedDate) mark(this.calEl || this.dateInput);

      // Slot: Wahrheit ist das versteckte slot_id-Feld bzw. selectedSlot
      const slotChosen = !!(this.slotIdInput.value || (this.selectedSlot && this.selectedSlot.id));
      if (!slotChosen) mark(this.slotsEl);

      // alle übrigen Pflichtfelder
      this.form.querySelectorAll('[required]').forEach((field) => {
        if (field === this.slotIdInput) return; // oben behandelt
        let ok;
        if (field.type === 'radio') {
          ok = !!this.form.querySelector('input[name="' + field.name + '"]:checked');
        } else if (field.type === 'checkbox') {
          ok = field.checked;
        } else {
          ok = !!(field.value && field.value.trim());
        }
        if (ok) return;
        if (field.type === 'radio')        mark(field.closest('.fb-pills, .fb-radios') || field);
        else if (field.type === 'checkbox') mark(field.closest('.fb-consent') || field);
        else                                mark(field);
      });

      return invalid;
    }

    clearFieldError(field) {
      const ok = field.type === 'checkbox'
        ? field.checked
        : field.type === 'radio'
        ? !!this.form.querySelector('input[name="' + field.name + '"]:checked')
        : !!(field.value && field.value.trim());
      if (!ok) return;
      field.classList.remove('fb-invalid');
      const grp = field.closest('.fb-pills, .fb-radios, .fb-consent');
      if (grp) grp.classList.remove('fb-invalid');
    }

    collectProperties() {
      const fd = new FormData(this.form);
      const map = {
        'Datum':          this.selectedDate,
        'Slot':           this.selectedSlot.label,
        '_Slot-ID':       this.selectedSlot.id,
        ...(this.selectedSlot.holdId ? { '_Hold-ID': this.selectedSlot.holdId } : {}),
        'Umfang': this.duration === 'full_bag' ? 'Full Bag Fitting (120 Min.)' : 'Kategorie-Fitting (60 Min.)',
        'Fokus':          fd.get('focus')      || '',
        'Schlaghand':     fd.get('hand')       || '',
        'Handicap':       fd.get('handicap')   || '',
        'Spielerfahrung': fd.get('experience') || '',
        'Name': ((fd.get('firstname') || '') + ' ' + (fd.get('lastname') || '')).trim(),
        'E-Mail':   fd.get('email')   || '',
        'Telefon':  fd.get('phone')   || '',
        'Adresse': [fd.get('street'), ((fd.get('zip') || '') + ' ' + (fd.get('city') || '')).trim(), fd.get('country')]
          .filter(Boolean).join(', '),
        'Location': this.locationId
      };
      const optional = {
        'Aktuelle Schläger': fd.get('current_clubs'),
        'Körpergröße (cm)':  fd.get('height_cm'),
        'Anliegen':          fd.get('message')
      };
      Object.entries(optional).forEach(([k, v]) => {
        if (v && String(v).trim()) map[k] = v;
      });
      return map;
    }

    async addToCart(variantId, properties) {
      const cartAdd = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
      const res = await fetch(cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties }] })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || data.message || this.texte.errorStatus.replace('[status]', res.status));
      }
    }

    setStatus(msg, isError = false) {
      this.statusEl.textContent = msg;
      this.statusEl.classList.toggle('fb-status--error', !!isError);
    }

    lockSubmit(locked) {
      this.submitBtn.disabled      = locked;
      this.submitLabel.textContent = locked ? this.texte.pleaseWait : this.texte.submit;
    }
  }

  customElements.define('fitting-booking-form', FittingBookingForm);
}

/* ─── Helpers ─── */

function pad2(n) { return String(n).padStart(2, '0'); }

function isoDate(d) {
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
}

function berlinDay(isoUtc) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(new Date(isoUtc));
}

function berlinToday() {
  return berlinDay(new Date().toISOString());
}

const FOCUS_OPTIONS = {
  '1h': [
    { value: '', label: 'Bitte auswählen' },
    { value: 'Driver', label: 'Driver' },
    { value: 'Eisen', label: 'Eisen' },
    { value: 'Wedges', label: 'Wedges' },
    { value: 'Fairway / Hybrid', label: 'Fairway / Hybrid' },
    { value: 'Putter', label: 'Putter' },
    { value: 'Nicht sicher, bitte beraten', label: 'Nicht sicher, bitte beraten' }
  ],
  full_bag: [
    { value: '', label: 'Bitte auswählen' },
    { value: 'Driver bis Wedges', label: 'Driver bis Wedges' },
    { value: 'Long Game: Driver, Fairway, Hybrid', label: 'Long Game: Driver, Fairway, Hybrid' },
    { value: 'Eisen + Wedges', label: 'Eisen + Wedges' },
    { value: 'Bag-Check / Kaufberatung', label: 'Bag-Check / Kaufberatung' },
    { value: 'Gapping / Distanzlücken', label: 'Gapping / Distanzlücken' },
    { value: 'Nicht sicher, bitte priorisieren', label: 'Nicht sicher, bitte priorisieren' }
  ]
};

function mockSlots(date, duration) {
  const dow = new Date(date).getDay();
  if (dow === 0) return [];
  const base = duration === 'full_bag'
    ? ['09:00–11:00', '11:00–13:00', '14:00–16:00']
    : ['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'];
  return base.map((label, i) => ({ id: 'mock-' + date + '-' + i, label, available: i !== 2 }));
}
